// Built-ins
import React, { useEffect, useMemo, useState } from "react";

// Modules / Functions
import axios from "axios";
import { FaPrint } from "react-icons/fa";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";

// Custom
import NavigationBar from "../../core/navigation_bar/navigation_bar";
import Footer from "../../core/footer/footer";
import { headers, pageHeader, loader, paginationOptions, formatDisplayDate } from "../../global_vars";
import AutocompleteSelect from "../../common/AutocompleteSelect";
import DateInput from "../../common/DateInput";
import { DOCUMENT_STATUS_CHOICES, documentStatusLabel } from "../../common/documentStatus";


// API endpoints
const ALL_DOCUMENTS = "https://ultima.icsgr.com/api/data_management/all_documents/";
const ALL_PROJECTS = "https://ultima.icsgr.com/api/data_management/projects/";

// Styling bits
const labelPillStyle = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: "10px",
  background: "#93ab3c",
  color: "#fff",
  fontSize: 12,
  fontWeight: 600,
  marginRight: 8,
  border: "1px solid #93ab3c",
};

function DocumentsReport() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [documents, setDocuments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");

  // Filters per spec: doc Status, Valid until, projects Status (In Progress), Project, Client (fullname contains), Original, Trafficable
  const [filters, setFilters] = useState({
    status: "",
    validUntilFrom: "",
    validUntilTo: "",
    projectStatus: "", // hint default empty; user may pick Inprogress
    project: "",
    client: "",
    original: "",
    trafficable: "",
  });

  useEffect(() => {
    const currentHeaders = { ...headers, Authorization: "Token " + localStorage.getItem("userToken") };
    async function load() {
      try {
        const [dRes, pRes] = await Promise.all([
          axios.get(ALL_DOCUMENTS, { headers: currentHeaders }),
          axios.get(ALL_PROJECTS, { headers: currentHeaders }).catch(() => null),
        ]);
        const allD = dRes?.data?.all_documents || dRes?.data?.results || dRes?.data?.data || dRes?.data || [];
        const allP = pRes?.data?.all_projects || pRes?.data?.results || pRes?.data?.data || pRes?.data || [];
        setDocuments(Array.isArray(allD) ? allD : []);
        setProjects(Array.isArray(allP) ? allP : []);
        setLoadError("");
        setIsLoaded(true);
      } catch (e) {
        const msg = e?.response?.data?.error || e?.response?.data?.detail || "Could not load documents.";
        setLoadError(typeof msg === "string" ? msg : "Could not load documents.");
        setDocuments([]);
        setIsLoaded(true);
      }
    }
    load();
  }, []);

  function setF(key, val) {
    setFilters((prev) => ({ ...prev, [key]: val }));
  }

  const projectById = useMemo(() => {
    const map = {};
    (projects || []).forEach((p) => { map[p.project_id] = p; });
    return map;
  }, [projects]);

  const filteredDocuments = useMemo(() => {
    const status = filters.status || "";
    const from = filters.validUntilFrom ? new Date(filters.validUntilFrom) : null;
    const to = filters.validUntilTo ? new Date(filters.validUntilTo) : null;
    const projectStatus = filters.projectStatus || "";
    const projectId = filters.project || "";
    const client = (filters.client || "").toLowerCase();
    const q = (search || "").trim().toLowerCase();

    function clientBits(c) {
      if (!c || typeof c !== "object") return [];
      return [c.surname, c.name, c.fullname, c.client_id];
    }

    function docPasses(d) {
      if (status && (d.status || "") !== status) return false;
      if (from || to) {
        if (!d.validuntil) return false;
        const vu = new Date(d.validuntil);
        if (!isFinite(vu)) return false;
        if (from && vu < from) return false;
        if (to && vu > to) return false;
      }
      if (projectStatus) {
        const projectRef = d.project;
        const pid = projectRef?.project_id || projectRef;
        const p = projectById[pid];
        if (!p || (p.status || "") !== projectStatus) return false;
      }
      if (projectId) {
        const pid = (d.project?.project_id || d.project || "");
        if (String(pid) !== String(projectId)) return false;
      }
      if (client) {
        const full = [...clientBits(d.client), ...(Array.isArray(d.related_clients) ? d.related_clients.flatMap(clientBits) : [])]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!full.includes(client)) return false;
      }
      if (q) {
        const hay = [
          d.document_id,
          d.title,
          d.notes,
          d.category?.title,
          d.project?.title,
          d.project?.filecode,
          documentStatusLabel(d.status),
          ...clientBits(d.client),
          ...(Array.isArray(d.related_clients) ? d.related_clients.flatMap(clientBits) : []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filters.original) {
        const want = filters.original === 'yes';
        if (!!d.original !== want) return false;
      }
      if (filters.trafficable) {
        const want = filters.trafficable === 'yes';
        if (!!d.trafficable !== want) return false;
      }
      return true;
    }

    return (documents || []).filter(docPasses);
  }, [documents, filters, projectById, search]);

  const columns = [
    {
      dataField: "document_id",
      text: "Document ID",
      sort: true,
      formatter: (cell, row) => (
        <a href={`/data_management/document/${row.document_id}`} id="cell_link">{row.document_id}</a>
      ),
    },
    { dataField: "title", text: "Title", sort: true },
    {
      dataField: "category.title",
      text: "Category",
      sort: true,
      formatter: (c, r) => r.category?.title || "Uncategorized",
    },
    { dataField: "project.title", text: "Project", sort: true, formatter: (c, r) => r.project?.title || "" },
    { dataField: "_client", text: "Client", sort: true, sortValue: (c, r) => `${r.client?.surname || ''} ${r.client?.name || ''}`.trim(), formatter: (c, r) => `${r.client?.surname || ''} ${r.client?.name || ''}`.trim() },
    { dataField: "created", text: "Created", sort: true, formatter: (c, r) => (r.created ? formatDisplayDate(r.created) : "") },
    { dataField: "validuntil", text: "Valid until", sort: true, formatter: (c, r) => (r.validuntil ? formatDisplayDate(r.validuntil) : "") },
    {
      dataField: "status",
      text: "Status",
      sort: true,
      formatter: (c, r) => documentStatusLabel(r.status) || "",
    },
    { dataField: "original", text: "Original", sort: true, formatter: (c, r) => (r.original ? "Yes" : "No") },
    { dataField: "trafficable", text: "Trafficable", sort: true, formatter: (c, r) => (r.trafficable ? "Yes" : "No") },
  ];

  return (
    <>
      <NavigationBar />
      <div className="mainContainer">
        {pageHeader("reports_documents", "Reports — Documents")}
        <div className="contentContainer">
          <div className="contentBody">
            {isLoaded ? (
              <>
                <style>{`.pillLink { color: inherit; text-decoration: none; } .pillLink:hover { color: #93ab3c; text-decoration: none; }`}</style>
                <div style={{ marginBottom: 16, background: '#fff', padding: 16, borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #eee' }}>
                  {loadError ? (
                    <div style={{ marginBottom: 12, color: "#a40000" }}>{loadError}</div>
                  ) : null}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px 16px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>Search</span>
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Title, file, client, project…"
                        style={{ flex: 1, minWidth: 180 }}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>Status</span>
                      <AutocompleteSelect value={filters.status} onChange={(v) => setF('status', v)} placeholder="Any" compact style={{ minWidth: 180, flex: 1 }}>
                        {DOCUMENT_STATUS_CHOICES.map((choice) => (
                          <option key={choice.value} value={choice.value}>
                            {choice.label}
                          </option>
                        ))}
                      </AutocompleteSelect>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>Valid until from</span>
                      <DateInput value={filters.validUntilFrom} onChange={(e) => setF('validUntilFrom', e.target.value)} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>Valid until to</span>
                      <DateInput value={filters.validUntilTo} onChange={(e) => setF('validUntilTo', e.target.value)} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>Project status</span>
                      <AutocompleteSelect value={filters.projectStatus} onChange={(v) => setF('projectStatus', v)} placeholder="Any" compact style={{ minWidth: 180, flex: 1 }}>
                        <option value="Created">Created</option>
                        <option value="Assigned">Assigned</option>
                        <option value="Inprogress">In Progress</option>
                        <option value="Completed">Completed</option>
                        <option value="Settled">Settled</option>
                        <option value="Abandoned">Abandoned</option>
                      </AutocompleteSelect>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>Project</span>
                      <AutocompleteSelect value={filters.project} onChange={(v) => setF('project', v)} placeholder="Any" compact style={{ minWidth: 180, flex: 1 }}>
                        {projects.map((p) => (
                          <option key={p.project_id} value={p.project_id}>{p.title}</option>
                        ))}
                      </AutocompleteSelect>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>Client (fullname)</span>
                      <input type="text" value={filters.client} onChange={(e) => setF('client', e.target.value)} placeholder="contains..." />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>Original</span>
                      <AutocompleteSelect value={filters.original} onChange={(v) => setF('original', v)} placeholder="Any" compact style={{ minWidth: 180, flex: 1 }}>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </AutocompleteSelect>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={labelPillStyle}>Trafficable</span>
                      <AutocompleteSelect value={filters.trafficable} onChange={(v) => setF('trafficable', v)} placeholder="Any" compact style={{ minWidth: 180, flex: 1 }}>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </AutocompleteSelect>
                    </div>
                  </div>
                </div>

                <ToolkitProvider bootstrap4 keyField="document_id" data={filteredDocuments} columns={columns} exportCSV={{ fileName: 'documents_report.csv' }} search condensed>
                  {(props) => (
                    <div>
                      <BootstrapTable
                        {...props.baseProps}
                        pagination={paginationFactory(paginationOptions)}
                        defaultSorted={[{ dataField: 'validuntil', order: 'asc' }]}
                        hover
                        bordered={false}
                        striped
                      />
                      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                        <button onClick={() => window.print()} style={{ marginLeft: 20, padding: '6px 10px', borderRadius: 6, border: '1px solid #444', background: '#444', color: '#fff', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <FaPrint /> Print / PDF
                        </button>
                      </div>
                    </div>
                  )}
                </ToolkitProvider>
              </>
            ) : (
              <div>{loader()}</div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default DocumentsReport;


